import * as React from 'react';

import hourglassIcon from '../../assets/images/hourglass.svg';
import { cn } from '../../lib/utils';
import { Tooltip } from '../Tooltip';

export interface SidebarNavItemProps {
  /** Icon node rendered at the left of the row (default/enabled state). */
  icon: React.ReactNode;
  /** Icon node used when `active` is true. Falls back to `icon` if omitted. */
  iconActive?: React.ReactNode;
  /** Icon node used when `disabled` is true. Falls back to `icon` if omitted. */
  iconDisabled?: React.ReactNode;
  label: string;
  active: boolean;
  disabled: boolean;
  /** Mirrors the sidebar's open/closed state — controls layout and tooltip. */
  collapsed: boolean;
  onClick: () => void;
  className?: string;
}

const TOOLTIP_ID_PREFIX = 'sidebar-nav-item-tooltip-';

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  icon,
  iconActive,
  iconDisabled,
  label,
  active,
  disabled,
  collapsed,
  onClick,
  className,
}) => {
  const [hovered, setHovered] = React.useState(false);

  // Stable id per-instance for aria-describedby wiring
  const tooltipId = React.useId();
  const ariaDescribedById = collapsed && hovered ? `${TOOLTIP_ID_PREFIX}${tooltipId}` : undefined;

  const handleClick = () => {
    if (disabled) return;
    onClick();
  };

  const displayedIcon =
    disabled && iconDisabled ? iconDisabled : active && iconActive ? iconActive : icon;

  // Active (expanded) = Figma "Selected": #2F2F2F row bg + 3px orange selection line on the left.
  // Active (collapsed) = just the orange icon swap — no row bg, no selection line (per Figma node 289:41021).
  // Hover (per design.md § 3.9 / HP-6 / Figma 289:34385) = SOLID #2F2F2F on the row +
  // /50 inner backplate behind the 24×24 icon container. The /50 used to be on the
  // row itself; that visual is now the inner-backplate-only treatment.
  const containerClasses = cn(
    'relative flex items-center h-[40px] rounded-[4px] transition-colors',
    collapsed ? 'justify-center w-full' : 'w-[202px]',
    active && !collapsed && 'bg-[#2F2F2F]',
    !active && !disabled && hovered && 'bg-[#2F2F2F]',
    disabled ? 'cursor-default' : 'cursor-pointer',
    className
  );

  // Inner 24×24 icon backplate. On hover (when not active and not disabled),
  // tint with /50 over the solid row bg so the icon sits in a visible plate.
  const iconBackplateBg = !active && !disabled && hovered ? 'bg-[#2F2F2F]/50' : '';

  const labelColorClass = active
    ? 'text-dawn-orange font-medium'
    : disabled
      ? 'text-[#525252] font-normal'
      : 'text-white font-normal';

  const selectionLine =
    active && !collapsed ? (
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 h-[40px] w-[3px] bg-dawn-orange rounded-[20px]"
      />
    ) : null;

  if (collapsed) {
    return (
      <div className="relative">
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          aria-label={label}
          aria-describedby={ariaDescribedById}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className={containerClasses}
        >
          {selectionLine}
          <div className="flex items-center justify-center h-full flex-1 px-2">
            <div
              className={cn(
                'w-[24px] h-[24px] flex items-center justify-center rounded-[4px]',
                iconBackplateBg
              )}
            >
              {displayedIcon}
            </div>
          </div>
        </button>
        <Tooltip
          label={label}
          show={hovered}
          placement="right"
          id={`${TOOLTIP_ID_PREFIX}${tooltipId}`}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={containerClasses}
    >
      {selectionLine}
      <div className="flex items-center h-full flex-1 px-2 gap-1">
        <div
          className={cn(
            'w-[24px] h-[24px] flex items-center justify-center shrink-0 rounded-[4px]',
            iconBackplateBg
          )}
        >
          {displayedIcon}
        </div>
        <span
          className={cn('font-outfit text-[12px] leading-[1.4] whitespace-nowrap', labelColorClass)}
        >
          {label}
        </span>
        {disabled && (
          <img
            src={hourglassIcon}
            alt=""
            aria-label="Coming soon"
            className="w-[16px] h-[16px] shrink-0 ml-auto"
          />
        )}
      </div>
    </button>
  );
};

SidebarNavItem.displayName = 'SidebarNavItem';
